import { Box, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import GlobalAnnouncementCenter from "src/components/blog/GlobalAnnouncementCenter";
import DvaInterceptorHost from "src/components/DvaInterceptorHost";
import LoadingScreen from "src/components/LoadingScreen";
import { resetLoading } from "src/slices/settings";
import { useDispatch, useSelector } from "src/store";
import { getDenied, subscribeDenied } from "src/utils/permissionDebugStore";
import NotFoundView from "src/views/errors/NotFoundView";
import NavBar from "./NavBar";
import TopBar from "./TopBar";

// Lazy — su bundle solo se descarga cuando se produce la primera denegación
const LazySecurityGuard = lazy(() => import("src/components/SecurityGuard"));

/**
 * Micro-bridge: suscribe al permissionDebugStore sin ningún overhead de MUI.
 * Solo renderiza el guardia (lazy) cuando hay al menos una denegación Y adminMode
 * está activo. El bundle de SecurityGuard nunca se descarga en sesiones normales.
 */
const SecurityGuardBridge = () => {
  const adminMode = useSelector((state) => state.settings.adminMode);
  const [hasDenied, setHasDenied] = useState(() => getDenied().length > 0);

  useEffect(() => {
    const unsub = subscribeDenied((list) => setHasDenied(list.length > 0));
    // Sincronizar con el estado actual por si ya hubo denegaciones antes del mount
    setHasDenied(getDenied().length > 0);
    return unsub;
  }, []);

  if (!adminMode || !hasDenied) return null;

  return (
    <Suspense fallback={null}>
      <LazySecurityGuard />
    </Suspense>
  );
};

const APP_BAR_HEIGHT = { xs: "56px", md: "58px" };

const roundMs = (value) => Math.round(Number(value || 0));

const buildLoadingDebugReport = (
  events = [],
  activeSources = {},
  nowTs = Date.now(),
) => {
  const orderedEvents = Array.isArray(events) ? [...events].reverse() : [];
  const openBySource = new Map();
  const statsBySource = new Map();

  const ensureStats = (source) => {
    if (!statsBySource.has(source)) {
      statsBySource.set(source, {
        source,
        sessions: 0,
        totalMs: 0,
        maxMs: 0,
        lastMs: 0,
        lastFinishedAt: null,
        unmatchedStops: 0,
      });
    }
    return statsBySource.get(source);
  };

  orderedEvents.forEach((event) => {
    const source = event?.source || "unknown";
    const at = Number(event?.at || nowTs);

    if (event?.type === "start") {
      const stack = openBySource.get(source) || [];
      stack.push(at);
      openBySource.set(source, stack);
      ensureStats(source);
      return;
    }

    if (event?.type === "stop") {
      const stack = openBySource.get(source) || [];
      const startedAt = stack.pop();
      openBySource.set(source, stack);

      const stat = ensureStats(source);
      if (!startedAt) {
        stat.unmatchedStops += 1;
        return;
      }

      const duration = Math.max(0, at - startedAt);
      stat.sessions += 1;
      stat.totalMs += duration;
      stat.maxMs = Math.max(stat.maxMs, duration);
      stat.lastMs = duration;
      stat.lastFinishedAt = at;
    }
  });

  const summaryRows = [...statsBySource.values()]
    .map((stat) => ({
      source: stat.source,
      sessions: stat.sessions,
      total_ms: roundMs(stat.totalMs),
      avg_ms: stat.sessions ? roundMs(stat.totalMs / stat.sessions) : 0,
      max_ms: roundMs(stat.maxMs),
      last_ms: roundMs(stat.lastMs),
      unmatched_stops: stat.unmatchedStops,
      last_finished_at: stat.lastFinishedAt
        ? new Date(stat.lastFinishedAt).toLocaleTimeString()
        : "-",
    }))
    .sort((a, b) => b.total_ms - a.total_ms);

  const activeRows = Object.entries(activeSources || {})
    .filter(([, payload]) => payload && payload.count > 0)
    .map(([source, payload]) => {
      const startedAt = Number(
        payload?.startedAt || payload?.lastEventAt || nowTs,
      );
      const activeMs = Math.max(0, nowTs - startedAt);
      const stack = openBySource.get(source) || [];
      return {
        source,
        active_count: payload.count,
        active_ms: roundMs(activeMs),
        stack_open_events: stack.length,
        message: payload.message || "-",
        last_event_at: payload.lastEventAt
          ? new Date(payload.lastEventAt).toLocaleTimeString()
          : "-",
      };
    })
    .sort((a, b) => b.active_ms - a.active_ms);

  const totals = summaryRows.reduce(
    (acc, row) => {
      acc.sessions += row.sessions;
      acc.totalMs += row.total_ms;
      return acc;
    },
    { sessions: 0, totalMs: 0 },
  );

  return {
    generatedAt: nowTs,
    summaryRows,
    activeRows,
    totals,
  };
};

const buildRequestPerfReport = (events = []) => {
  const safeEvents = Array.isArray(events) ? events : [];
  const statsByEndpoint = new Map();

  safeEvents.forEach((event) => {
    const method = (event?.method || "GET").toUpperCase();
    const url = event?.url || "unknown";
    const key = `${method} ${url}`;
    const durationMs = Math.max(0, Number(event?.durationMs || 0));

    if (!statsByEndpoint.has(key)) {
      statsByEndpoint.set(key, {
        endpoint: key,
        method,
        url,
        count: 0,
        totalMs: 0,
        maxMs: 0,
        minMs: Number.POSITIVE_INFINITY,
        errorCount: 0,
      });
    }

    const stat = statsByEndpoint.get(key);
    stat.count += 1;
    stat.totalMs += durationMs;
    stat.maxMs = Math.max(stat.maxMs, durationMs);
    stat.minMs = Math.min(stat.minMs, durationMs);
    if (event?.ok === false) {
      stat.errorCount += 1;
    }
  });

  const summaryRows = [...statsByEndpoint.values()]
    .map((stat) => ({
      endpoint: stat.endpoint,
      count: stat.count,
      total_ms: roundMs(stat.totalMs),
      avg_ms: stat.count ? roundMs(stat.totalMs / stat.count) : 0,
      max_ms: roundMs(stat.maxMs),
      min_ms: Number.isFinite(stat.minMs) ? roundMs(stat.minMs) : 0,
      error_count: stat.errorCount,
    }))
    .sort((a, b) => b.total_ms - a.total_ms);

  const totals = summaryRows.reduce(
    (acc, row) => {
      acc.count += row.count;
      acc.totalMs += row.total_ms;
      acc.errorCount += row.error_count;
      return acc;
    },
    { count: 0, totalMs: 0, errorCount: 0 },
  );

  return {
    summaryRows,
    totals,
  };
};

const simplifyResourceName = (rawName = "") => {
  if (!rawName) {
    return "unknown";
  }

  if (typeof window === "undefined") {
    return rawName;
  }

  try {
    const parsed = new URL(rawName, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (_error) {
    return rawName;
  }
};

const buildBrowserPerfReport = () => {
  if (typeof performance === "undefined") {
    return {
      navigation: null,
      paint: {},
      resourceTopRows: [],
      resourceTypeRows: [],
      resourceTotals: { count: 0, totalMs: 0 },
      runtime: {
        longTasks: { count: 0, totalMs: 0, maxMs: 0 },
        lcp: null,
        cls: null,
      },
    };
  }

  const navigationEntry = performance.getEntriesByType("navigation")?.[0];
  const navigation = navigationEntry
    ? {
        dns_ms: roundMs(
          navigationEntry.domainLookupEnd - navigationEntry.domainLookupStart,
        ),
        tcp_ms: roundMs(
          navigationEntry.connectEnd - navigationEntry.connectStart,
        ),
        tls_ms:
          navigationEntry.secureConnectionStart > 0
            ? roundMs(
                navigationEntry.connectEnd -
                  navigationEntry.secureConnectionStart,
              )
            : 0,
        ttfb_ms: roundMs(navigationEntry.responseStart),
        response_ms: roundMs(
          navigationEntry.responseEnd - navigationEntry.responseStart,
        ),
        dom_interactive_ms: roundMs(navigationEntry.domInteractive),
        dom_content_loaded_ms: roundMs(
          navigationEntry.domContentLoadedEventEnd,
        ),
        load_event_ms: roundMs(navigationEntry.loadEventEnd),
      }
    : null;

  const paintEntries = performance.getEntriesByType("paint") || [];
  const paint = paintEntries.reduce((acc, entry) => {
    acc[entry.name] = roundMs(entry.startTime);
    return acc;
  }, {});

  const resourceEntries = (
    performance.getEntriesByType("resource") || []
  ).filter((entry) => Number(entry.duration || 0) > 0);

  const resourceTopRows = resourceEntries
    .map((entry) => ({
      name: simplifyResourceName(entry.name),
      initiator_type: entry.initiatorType || "other",
      duration_ms: roundMs(entry.duration),
      transfer_size_kb:
        typeof entry.transferSize === "number"
          ? roundMs(entry.transferSize / 1024)
          : 0,
      encoded_size_kb:
        typeof entry.encodedBodySize === "number"
          ? roundMs(entry.encodedBodySize / 1024)
          : 0,
    }))
    .sort((a, b) => b.duration_ms - a.duration_ms)
    .slice(0, 30);

  const resourceTypeStats = new Map();
  resourceEntries.forEach((entry) => {
    const type = entry.initiatorType || "other";
    if (!resourceTypeStats.has(type)) {
      resourceTypeStats.set(type, {
        initiator_type: type,
        count: 0,
        totalMs: 0,
        maxMs: 0,
      });
    }

    const stat = resourceTypeStats.get(type);
    stat.count += 1;
    stat.totalMs += Number(entry.duration || 0);
    stat.maxMs = Math.max(stat.maxMs, Number(entry.duration || 0));
  });

  const resourceTypeRows = [...resourceTypeStats.values()]
    .map((stat) => ({
      initiator_type: stat.initiator_type,
      count: stat.count,
      total_ms: roundMs(stat.totalMs),
      avg_ms: stat.count ? roundMs(stat.totalMs / stat.count) : 0,
      max_ms: roundMs(stat.maxMs),
    }))
    .sort((a, b) => b.total_ms - a.total_ms);

  const resourceTotals = resourceTypeRows.reduce(
    (acc, row) => {
      acc.count += row.count;
      acc.totalMs += row.total_ms;
      return acc;
    },
    { count: 0, totalMs: 0 },
  );

  const runtimePerf = window.__arkaduRuntimePerf || {};
  const longTasks = Array.isArray(runtimePerf.longTasks)
    ? runtimePerf.longTasks
    : [];
  const longTaskSummary = longTasks.reduce(
    (acc, task) => {
      const duration = Number(task?.duration || 0);
      acc.count += 1;
      acc.totalMs += duration;
      acc.maxMs = Math.max(acc.maxMs, duration);
      return acc;
    },
    { count: 0, totalMs: 0, maxMs: 0 },
  );

  const lcpEntries = Array.isArray(runtimePerf.lcpEntries)
    ? runtimePerf.lcpEntries
    : [];
  const latestLcp = lcpEntries.length
    ? lcpEntries[lcpEntries.length - 1]
    : null;

  return {
    navigation,
    paint,
    resourceTopRows,
    resourceTypeRows,
    resourceTotals,
    runtime: {
      longTasks: {
        count: longTaskSummary.count,
        totalMs: roundMs(longTaskSummary.totalMs),
        maxMs: roundMs(longTaskSummary.maxMs),
      },
      lcp: latestLcp
        ? {
            time_ms: roundMs(latestLcp.startTime),
            element: latestLcp.element || null,
          }
        : null,
      cls:
        typeof runtimePerf.cls === "number"
          ? Number(runtimePerf.cls.toFixed(4))
          : null,
    },
  };
};

const DashboardLayout = ({ children }) => {
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up("lg"));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customerName } = useParams();
  const pathname = window.location.pathname;
  const isRegistrationPath = pathname.includes("account/registration");
  const DRAWER_WIDTH = isRegistrationPath ? 0 : 276;

  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const initialized = useRef(false);

  // Select only what we need to minimize rerenders
  const customerError = useSelector((state) => state.customers.customerError);
  const currentCustomer = useSelector(
    (state) => state.customers.currentCustomer,
  );
  const adminMode = useSelector((state) => state.settings.adminMode);
  const isLoading = useSelector((state) => state.settings.isLoading);
  const loadingMessage = useSelector((state) => state.settings.loadingMessage);
  const loadingEvents = useSelector((state) => state.settings.loadingEvents);
  const user = useSelector((state) => state.users.user);
  const loadingActiveSources = useSelector(
    (state) => state.settings.loadingActiveSources,
  );
  const loadingLastSource = useSelector(
    (state) => state.settings.loadingLastSource,
  );

  const activeSourceRows = useMemo(
    () =>
      Object.entries(loadingActiveSources || {})
        .filter(([, value]) => value && value.count > 0)
        .sort((a, b) => (b[1].lastEventAt || 0) - (a[1].lastEventAt || 0))
        .slice(0, 6),
    [loadingActiveSources],
  );

  const formatEventTime = useCallback((timestamp) => {
    if (!timestamp) return "-";
    try {
      return new Date(timestamp).toLocaleTimeString();
    } catch (_error) {
      return "-";
    }
  }, []);

  const shouldConsoleDebugLoading = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const explicitDebug =
      searchParams.get("debugLoading") === "1" ||
      window.localStorage.getItem("debug:loading") === "1";

    if (process.env.NODE_ENV === "production") {
      return false;
    }

    if (explicitDebug) {
      return true;
    }

    return Boolean(adminMode && user?.is_superuser);
  }, [adminMode, user]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      dispatch(resetLoading());
    }
  }, [dispatch]);

  useEffect(() => {
    const buildAndPrintReport = () => {
      const freshReport = buildLoadingDebugReport(
        loadingEvents,
        loadingActiveSources,
      );
      const requestEvents = window.__arkaduRequestPerf?.events || [];
      const requestReport = buildRequestPerfReport(requestEvents);
      const browserReport = buildBrowserPerfReport();

      console.group("[loading] summary");
      console.table(freshReport.summaryRows);
      if (freshReport.activeRows.length) {
        console.table(freshReport.activeRows);
      }
      console.log("totals", freshReport.totals);
      console.groupEnd();

      console.group("[request-perf] summary");
      console.table(requestReport.summaryRows.slice(0, 25));
      console.log("totals", requestReport.totals);
      console.groupEnd();

      console.group("[browser-perf] summary");
      if (browserReport.navigation) {
        console.table([browserReport.navigation]);
      }
      if (Object.keys(browserReport.paint || {}).length) {
        console.table([browserReport.paint]);
      }
      if (browserReport.resourceTypeRows.length) {
        console.table(browserReport.resourceTypeRows);
      }
      if (browserReport.resourceTopRows.length) {
        console.table(browserReport.resourceTopRows.slice(0, 25));
      }
      console.log("resourceTotals", browserReport.resourceTotals);
      console.log("runtime", browserReport.runtime);
      console.groupEnd();

      return {
        ...freshReport,
        requestReport,
        browserReport,
      };
    };

    window.__arkaduLoadingDebugReport = buildAndPrintReport;
    const requestEvents = window.__arkaduRequestPerf?.events || [];
    const requestReport = buildRequestPerfReport(requestEvents);
    const browserReport = buildBrowserPerfReport();
    window.__arkaduLoadingDebug = {
      ...buildLoadingDebugReport(loadingEvents, loadingActiveSources),
      requestReport,
      browserReport,
      latestEvent: loadingEvents?.[0] || null,
      events: loadingEvents,
    };
  }, [loadingActiveSources, loadingEvents]);

  useEffect(() => {
    // Loading debug logs are intentionally disabled to keep production console clean.
  }, []);

  const handleOpenMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const handleCloseMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const rootSx = useMemo(
    () => ({
      position: "relative",
      display: "flex",
      width: "100%",
      height: "100%",
      minHeight: 0,
      overflow: "hidden",
      backgroundColor: theme.palette.background.default,
      backgroundImage: `
        radial-gradient(1100px 280px at 85% -140px, ${alpha(
          theme.palette.primary.main,
          theme.palette.mode === "dark" ? 0.2 : 0.08,
        )}, transparent 62%),
        radial-gradient(720px 220px at 10% 0%, ${alpha(
          theme.palette.secondary.main,
          theme.palette.mode === "dark" ? 0.1 : 0.05,
        )}, transparent 60%),
        linear-gradient(180deg, ${alpha(
          theme.palette.background.paper,
          theme.palette.mode === "dark" ? 0.32 : 0.9,
        )} 0%, ${theme.palette.background.default} 100%)`,
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `linear-gradient(${alpha(
          theme.palette.divider,
          theme.palette.mode === "dark" ? 0.08 : 0.05,
        )} 1px, transparent 1px), linear-gradient(90deg, ${alpha(
          theme.palette.divider,
          theme.palette.mode === "dark" ? 0.08 : 0.05,
        )} 1px, transparent 1px)`,
        backgroundSize: "32px 32px",
        maskImage: "linear-gradient(180deg, rgba(0,0,0,0.22), transparent 45%)",
      },
    }),
    [theme],
  );

  const wrapperSx = useMemo(
    () => ({
      display: "flex",
      flex: "1 1 auto",
      minHeight: 0,
      minWidth: 0,
      overflow: "hidden",
      pt: APP_BAR_HEIGHT,
      ...(upLg && { pl: `${DRAWER_WIDTH}px` }),
    }),
    [theme, upLg],
  );

  const contentContainerSx = useMemo(
    () => ({
      display: "flex",
      flex: "1 1 auto",
      minHeight: 0,
      minWidth: 0,
      overflow: "hidden",
    }),
    [],
  );

  const contentSx = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
      minWidth: 0,
      overflowY: "auto",
      overflowX: "hidden",
      scrollBehavior: "auto",
      WebkitOverflowScrolling: "touch",
      backfaceVisibility: "hidden",
      px: { xs: 1, md: 2.5, xl: 3 },
      pb: { xs: 1.5, md: 2.5 },
      gap: { xs: 1, md: 1.75 },
      width: "100%",
      maxWidth: "1800px",
      mx: "auto",
      fontFamily: `'Inter', 'Roboto', 'Helvetica Neue', Arial, sans-serif`,
    }),
    [theme],
  );

  if (customerName && customerError) {
    return <NotFoundView message={customerError} />;
  }

  return (
    <Box sx={rootSx}>
      {/* App top bar */}
      <TopBar onMobileNavOpen={handleOpenMobileNav} />

      {/* Side navigation */}
      {isRegistrationPath ? null : (
        <NavBar
          openMobile={isMobileNavOpen}
          onMobileClose={handleCloseMobileNav}
        />
      )}

      {/* Guardia de Seguridad Digital — solo carga su bundle si hay permisos denegados */}
      <SecurityGuardBridge />

      {/* Puente global DVA: pide mensaje requerido y avisa acciones encoladas */}
      <DvaInterceptorHost />

      <GlobalAnnouncementCenter />

      {/* Floating AI assistant temporalmente desactivado */}

      {/* Main wrapper */}
      <Box sx={wrapperSx}>
        <Box sx={contentContainerSx}>
          <Box component="main" sx={contentSx}>
            {children}
          </Box>
        </Box>
      </Box>

      {/* Loading overlay with tech glow - se monta solo en loading para evitar ghost overlay */}
      {isLoading && (
        <Box
          sx={{
            color: "#fff",
            zIndex: (t) => t.zIndex.modal + 2,
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
            backdropFilter: "blur(8px)",
            background:
              "linear-gradient(180deg, rgba(15,23,42,0.42) 0%, rgba(15,23,42,0.58) 100%)",
          }}
        >
          <LoadingScreen
            embedded
            withBackdrop
            suppressWhenGlobalLoading={false}
            title={
              loadingLastSource ? t("Cargando") : t("Procesando solicitud")
            }
            subtitle={
              currentCustomer?.name
                ? currentCustomer.name
                : t("Optimizando la experiencia para ti...")
            }
            message={loadingMessage || t("loading")}
            maxWidth={640}
            minHeight={0}
          />
        </Box>
      )}
    </Box>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node,
};

export default DashboardLayout;
